import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "app/components/ui/card";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "app/components/ui/chart";
import type { ChartConfig } from "app/components/ui/chart";

interface ProtocolIncomeData {
  timestamp: string;
  totalNewInterest: string;
  totalInterestPaid: string;
  totalValueClaimed: string;
  totalPrincipalDefaulted: string;
}

interface ProtocolIncomeChartProps {
  data: ProtocolIncomeData[];
}

const chartConfig = {
  interest: {
    label: "Interest",
    color: "hsl(var(--chart-1))",
  },
  income: {
    label: "Income",
    color: "hsl(var(--chart-2))",
  },
} satisfies ChartConfig;

const formatUSD = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

function formatDate(timestamp: string): string {
  try {
    const timestampNum = Number(timestamp);
    if (isNaN(timestampNum) || timestampNum <= 0) {
      console.warn("Invalid timestamp value:", timestamp);
      return "";
    }
    // Convert from microseconds to milliseconds
    const date = new Date(timestampNum / 1000);
    if (date.toString() === "Invalid Date") {
      console.warn("Invalid date from timestamp:", timestamp);
      return "";
    }
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.error("Error converting timestamp:", timestamp, error);
    return "";
  }
}

const ProtocolIncomeChart = ({ data }: ProtocolIncomeChartProps) => {
  const chartData = data.map((item) => {
    const totalInterest = parseFloat(item.totalNewInterest) + parseFloat(item.totalInterestPaid);
    const income = parseFloat(item.totalValueClaimed) - parseFloat(item.totalPrincipalDefaulted);
    return {
      date: formatDate(item.timestamp),
      interest: totalInterest,
      income: income,
    };
  }).filter(item => item.date); // Remove items with invalid dates

  return (
    <Card>
      <CardHeader>
        <CardTitle>Protocol Income</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ChartContainer config={chartConfig}>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={chartData}
              margin={{ top: 0, right: 10, bottom: 0, left: 12 }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => value.split("-").slice(1).join("/")}
                tickLine={false}
                axisLine={false}
              />
              <YAxis tickFormatter={formatUSD} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              {Object.entries(chartConfig).map(([key, config]) => (
                <Bar
                  key={key}
                  name={config.label}
                  dataKey={key}
                  stackId="a"
                  fill={`var(--color-${key})`}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

export default ProtocolIncomeChart; 